import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { ACCOUNT_CHANGED_EVENT } from '@/lib/axios';
import { isSocialLinkingPlatform } from '@/lib/account-settings/visibleSocialLinkingPlatforms';
import { parseSocialOauthCallbackMessage, resolveConfiguredApiOrigin, type SocialOauthPlatform } from '@/lib/account-settings/oauthCallbackMessage';
import { buildSocialOauthRelayPath, SOCIAL_OAUTH_WINDOW_FEATURES, SOCIAL_OAUTH_WINDOW_NAME } from '@/lib/account-settings/socialOauthRelay';

const POLL_INTERVAL_MS = 1500;
/** A reload hint, never evidence of successful linking or ownership. */
export const SOCIAL_CONNECTIONS_UPDATED_EVENT = 'autoclipper:social-connections-updated';
// All entry points reuse one named browser window. Supersede its prior owner
// so two mounted surfaces cannot both consume the same callback.
let cancelActiveAttempt: (() => void) | null = null;
type Attempt = { generation: number; platform: SocialOauthPlatform; popup: Window; token: string | null; cancel: () => void };
type Options = { enabled?: boolean; onRefresh?: () => unknown; pollWhileOpen?: boolean };

/** Opens synchronously in the click gesture; the relay owns API auth/navigation.
 * Only a matching callback from that popup on the configured API is trusted. */
export function useSocialAccountConnection({ enabled = true, onRefresh, pollWhileOpen = true }: Options = {}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const latest = useRef({ enabled, onRefresh, pollWhileOpen, t, toast });
  latest.current = { enabled, onRefresh, pollWhileOpen, t, toast };
  const alive = useRef(false);
  const generation = useRef(0);
  const active = useRef<Attempt | null>(null);
  const polling = useRef<number | null>(null);
  const [connectingPlatform, setConnectingPlatform] = useState<SocialOauthPlatform | null>(null);

  const clear = useCallback((expectedGeneration?: number) => {
    const attempt = active.current;
    if (expectedGeneration !== undefined && attempt?.generation !== expectedGeneration) return false;
    if (attempt && cancelActiveAttempt === attempt.cancel) cancelActiveAttempt = null;
    active.current = null;
    if (polling.current !== null) window.clearInterval(polling.current);
    polling.current = null;
    if (alive.current) setConnectingPlatform(null);
    return true;
  }, []);
  const isCurrent = useCallback((attempt: Attempt) => alive.current && latest.current.enabled
    && active.current === attempt && attempt.token === localStorage.getItem('token'), []);
  const refresh = useCallback((notifyOtherSurfaces = false) => {
    try { void Promise.resolve(latest.current.onRefresh?.()).catch(() => undefined); } catch {
      // The caller owns refresh errors; never turn them into provider messages.
    }
    if (notifyOtherSurfaces) window.dispatchEvent(new Event(SOCIAL_CONNECTIONS_UPDATED_EVENT));
  }, []);

  const connect = useCallback((platform: SocialOauthPlatform) => {
    if (!alive.current || !latest.current.enabled || !isSocialLinkingPlatform(platform)) return;
    cancelActiveAttempt?.(); clear();
    const relay = buildSocialOauthRelayPath(platform);
    let popup: Window | null = null;
    try { popup = window.open(relay, SOCIAL_OAUTH_WINDOW_NAME, SOCIAL_OAUTH_WINDOW_FEATURES); } catch { /* Same-tab fallback below. */ }
    if (!popup) { window.location.assign(relay); return; }
    const nextGeneration = ++generation.current;
    const attempt: Attempt = { generation: nextGeneration, platform, popup, token: localStorage.getItem('token'), cancel: () => { clear(nextGeneration); } };
    active.current = attempt;
    cancelActiveAttempt = attempt.cancel;
    setConnectingPlatform(platform);
    polling.current = window.setInterval(() => {
      if (!isCurrent(attempt)) { clear(attempt.generation); return; }
      let closed = true;
      try { closed = attempt.popup.closed; } catch { /* Inaccessible windows end local tracking. */ }
      if (closed) { clear(attempt.generation); refresh(true); }
      else if (latest.current.pollWhileOpen) refresh();
    }, POLL_INTERVAL_MS);
  }, [clear, isCurrent, refresh]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) clear();
    const apiOrigin = resolveConfiguredApiOrigin(import.meta.env.VITE_API_URL);
    const message = (event: MessageEvent) => {
      const attempt = active.current;
      if (!attempt || !isCurrent(attempt) || !apiOrigin || event.origin !== apiOrigin || event.source !== attempt.popup) return;
      const callback = parseSocialOauthCallbackMessage(event.data);
      if (!callback || callback.platform !== attempt.platform || !clear(attempt.generation)) return;
      const { t: translate, toast: notify } = latest.current;
      if (callback.kind === 'success') {
        refresh(true);
        notify({ title: translate('account.socialConnected'), description: translate('account.socialConnectedDescription') });
      } else {
        notify({ title: translate('account.socialConnectionFailed'), description: translate(callback.messageKey), variant: 'destructive' });
      }
    };
    const changed = () => { clear(); };
    if (enabled) { window.addEventListener('message', message); window.addEventListener(ACCOUNT_CHANGED_EVENT, changed); }
    return () => {
      alive.current = false; clear();
      window.removeEventListener('message', message); window.removeEventListener(ACCOUNT_CHANGED_EVENT, changed);
    };
  }, [clear, enabled, isCurrent, refresh]);
  return { connect, connectingPlatform };
}
